import { useEffect } from 'react';
import { useTranslations } from 'next-intl';
import { useFormContext } from 'react-hook-form';
import {
  Conditional,
  DealerLocatorCancelButton,
  DealerLocatorConfirmButton,
  DealerLocatorFiltersClearServiceOfferingsButton,
  DealerLocatorFiltersHeader,
  DealerLocatorFiltersServiceOfferings,
  DealerLocatorFiltersServiceOfferingsChips,
  DealerLocatorFiltersSortDropdown,
  DealerLocatorHeader,
  Drawer,
  DrawerBody,
  DrawerFooter,
  useDrawer
} from 'components/pcc-components';
import { CatInlineNotification } from 'blocks-nextjs/blocks-react/components/InlineNotification';
import { CatIconWarningCircle } from 'blocks-nextjs/blocks-react/components/Icons/WarningCircle';
import { OPTIONS, SORT_ORDER } from '../constants';
import { Filters, HandleFiltersSubmitProps } from '../types';

interface DealerLocatorFiltersDrawerProps {
  defaultFilterOptionsValues: boolean[];
  filtersDrawer: ReturnType<typeof useDrawer>;
  filters: Filters;
  resetStoreId: () => void;
  sortingOptions: { label: string; value: string }[];
  showFilterErrorMessage: boolean;
  setAreFiltersLoading: (value: boolean) => void;
  setFilters: (filters: { [key: string]: boolean | string }) => void;
  setShouldCloseEntireDL: (value: boolean) => void;
}

export const DealerLocatorFiltersDrawer = ({
  defaultFilterOptionsValues,
  filtersDrawer,
  filters,
  resetStoreId,
  sortingOptions,
  showFilterErrorMessage,
  setAreFiltersLoading,
  setFilters,
  setShouldCloseEntireDL
}: DealerLocatorFiltersDrawerProps) => {
  const t = useTranslations();
  const { formState, handleSubmit, setValue } = useFormContext();
  const backToSelectStoreButtonMessage = t('GENERAL_BACK_TO', {
    0: t('CAT_SELECT_STORE')
  });

  useEffect(() => {
    setShouldCloseEntireDL(false);
  }, [setShouldCloseEntireDL]);

  const options = [
    t('SERVICES_MACHINE'),
    t('SERVICES_ENGINE_SERVICE'),
    t('SERVICES_POWER_SYSTEMS'),
    t('SERVICES_RENTALS'),
    t('SERVICES_EQUIPT_SALES'),
    t('SERVICES_EQUIPT_SERVICE'),
    t('SERVICES_PARTS_PICKUP'),
    t('SERVICES_PARTS_SALES'),
    t('SERVICES_MARINE'),
    t('SERVICES_TECHNOLOGY_SOLUTIONS')
  ];

  const handleFiltersSubmit = (data: HandleFiltersSubmitProps) => {
    setAreFiltersLoading(true);
    const filtersData = {
      sortOrder: data.sortOrder.value,
      Machine: data.options[0],
      'Engine Services': data.options[1],
      'Power Systems': data.options[2],
      Rentals: data.options[3],
      'Equipment Sales': data.options[4],
      'Equipment Service': data.options[5],
      'Parts Pick-up': data.options[6],
      'Parts Sales': data.options[7],
      Marine: data.options[8],
      'Technology Solutions': data.options[9]
    };
    setFilters(filtersData);
    resetStoreId();
  };

  const handleCancelFilters = () => {
    const { sortOrder, ...options } = filters || {
      sortOrder: sortingOptions[0].value,
      ...defaultFilterOptionsValues
    };
    const sortOrderValue = sortingOptions.find(
      (option) => option.value === sortOrder
    );
    setValue(SORT_ORDER, sortOrderValue, { shouldDirty: true });
    setValue(OPTIONS, Object.values(options), {
      shouldDirty: true
    });
    filtersDrawer.closeDrawer();
  };

  const showClearFiltersButton = formState.isDirty;

  return (
    <form onSubmit={handleSubmit(handleFiltersSubmit)}>
      <Drawer className="u-color--Black" {...filtersDrawer.drawerProps}>
        <DealerLocatorHeader
          title={backToSelectStoreButtonMessage}
          onBackButtonClick={handleCancelFilters}
        />
        <DrawerBody>
          <DealerLocatorFiltersHeader>
            {t('FILTER_AND_SORT')}
          </DealerLocatorFiltersHeader>
          <Conditional test={showFilterErrorMessage}>
            <div className="pt-4 mt-md-2 pb-md-2">
              <CatInlineNotification
                variant="error"
                data-testid="filter-drawer-results-error"
              >
                <CatIconWarningCircle slot="before" data-testid="error-icon" />
                {`${t('FYD_NO_RESULTS')} ${t('FYD_ADJUST_SEARCH')}`}
              </CatInlineNotification>
            </div>
          </Conditional>
          <DealerLocatorFiltersSortDropdown
            title={t('SORT')}
            sortingOptions={sortingOptions}
          />
          <DealerLocatorFiltersServiceOfferings
            title={t('FYD_FILTER_SERVICES_OFFERED')}
          >
            <Conditional test={showClearFiltersButton}>
              <DealerLocatorFiltersClearServiceOfferingsButton>
                {t('CLEAR_ALL_FILTERS')}
              </DealerLocatorFiltersClearServiceOfferingsButton>
            </Conditional>
            <DealerLocatorFiltersServiceOfferingsChips options={options} />
          </DealerLocatorFiltersServiceOfferings>
        </DrawerBody>
        <DrawerFooter>
          <DealerLocatorConfirmButton>
            {t('APPLY_CHANGES')}
          </DealerLocatorConfirmButton>
          <DealerLocatorCancelButton onCancel={handleCancelFilters}>
            {t('CANCEL')}
          </DealerLocatorCancelButton>
        </DrawerFooter>
      </Drawer>
    </form>
  );
};
